import {
  GameScratchAdvancedData,
  GameScratchCardData,
  GameScratchGeneralData,
  GameScratchImagePoolData,
  GameScratchLayoutData
} from '@/components/games/scratchcard/Data';
import { GameIndicator, GameModel, InstantWinData } from '@/models/GameModel';
import { DrawType } from '@/typings/enums/enums';
import useDevice, { getDeviceData } from '@/hooks/useDevice';

export interface GameScratchCardState {
  general: GameScratchCardGeneralState;
  layout: GameScratchLayoutState;
  advanced: GameScratchCardAdvancedState;
  pool: GameScratchImagePoolState[];
  hasScratchTool: boolean;
  fields: GameScratchFieldsState[];
  winner: boolean;
  winnerImageId: string;
  winnerImage: string;
}

export interface GameScratchCardAdvancedSoundState {
  enabled: boolean;
  src?: string;
}

export interface GameScratchCardAdvancedState {
  sound: GameScratchCardAdvancedSoundState;
}

interface GameScratchCardGeneralState {
  drawType: DrawType;
  scratchfieldAmount: number;
  imagePoolAmount: number;
  scratchAreaSize?: number;
  winnerPercentage: number;
  winnerImage?: number | string; // number or string because it can contain 'all' as string
  scratchToolImage: string;
}

interface GameScratchLayoutState {
  scratchcardImage: string;
  coinImage?: string;
  coinOffsetX?: string;
  coinOffsetY?: string;
}

export interface GameScratch {
  x: number;
  y: number;
}

export interface GameScratchFieldsState {
  id: number;
  x: number;
  y: number;
  w: number;
  h: number;
  origW: number;
  origH: number;
}

export interface GameScratchImagePoolState {
  id: number;
  image: string;
}

export interface GameScratchInstantWin extends InstantWinData {
  winner_image?: string;
  winner_image_id?: string;
}

export class GameScratchCardModel extends GameModel<GameScratchCardData, GameScratchCardState> {
  parseGame(data: GameScratchCardData) {
    const state = this.state;

    state.general = GameScratchCardModel.constructGeneralState(data.general);

    state.layout = GameScratchCardModel.parseLayoutState(data);

    state.pool = GameScratchCardModel.parseImagePoolState(data.pool.pool);

    state.hasScratchTool = useDevice().isDesktop && !!state.layout.coinImage;

    state.fields = GameScratchCardModel.parseScratchFieldsState(data);

    state.advanced = state.advanced ?? {};

    state.advanced.sound = GameScratchCardModel.constructAdvancedSoundState(data?.advanced);
  }

  private static parseScratchFieldsState(data: GameScratchCardData): GameScratchFieldsState[] {
    const layoutData = getDeviceData<GameScratchLayoutData>(data.layout.layout);

    if (!layoutData) {
      return [];
    }

    return GameScratchCardModel.constructScratchFieldsState(layoutData);
  }

  private static constructScratchFieldsState(data: GameScratchLayoutData): GameScratchFieldsState[] {
    return data.place.map<GameScratchFieldsState>((place) => {
      return {
        id: Number(place.id),
        x: Number(place.x),
        y: Number(place.y),
        w: Number(place.w),
        h: Number(place.h),
        origW: Number(place.w),
        origH: Number(place.h)
      };
    });
  }

  private static parseImagePoolState(data: GameScratchImagePoolData) {
    const imagePool: GameScratchImagePoolState[] = [];

    for (const stateKey in data) {
      imagePool.push({ id: Number(stateKey), image: data[`${stateKey}`] });
    }

    return imagePool;
  }

  private static constructGeneralState(data: GameScratchGeneralData): GameScratchCardGeneralState {
    // using types here makes more sense then enums. default values are testet anyway.
    return {
      //
      scratchfieldAmount: Number(data.scratchfield_amount) || 3,
      imagePoolAmount: Number(data.image_pool_amount) || 4,
      drawType: data.draw_type,
      scratchAreaSize: Number(data.scratch_area_size) || 60,
      scratchToolImage: data.scratch_tool_image,
      winnerImage: data.winner_image,
      winnerPercentage: Number(data.winner_percentage) || 60
    };
  }

  private static parseLayoutState(data: GameScratchCardData): GameScratchLayoutState {
    const layoutData = getDeviceData<GameScratchLayoutData>(data.layout.layout);

    if (!layoutData && data.layout.layout.desktop) {
      return GameScratchCardModel.constructLayoutState({
        scratchcard_image: data.layout.layout.desktop?.scratchcard_image,
        place: data.layout.layout.desktop?.place
      });
    }
    if (layoutData) {
      return GameScratchCardModel.constructLayoutState(layoutData);
    }

    throw new Error('Game assets not available');
  }

  private static constructLayoutState(data: GameScratchLayoutData): GameScratchLayoutState {
    return {
      scratchcardImage: data.scratchcard_image,
      ...(data.coin_image && { coinImage: data.coin_image }),
      ...(data.coin_offset_x && { coinOffsetX: data.coin_offset_x }),
      ...(data.coin_offset_y && { coinOffsetY: data.coin_offset_y })
    };
  }

  private static constructAdvancedSoundState(
    data: GameScratchAdvancedData | undefined
  ): GameScratchCardAdvancedSoundState {
    return {
      enabled: !!data?.sound,
      ...(data?.sound && { src: data.sound })
    };
  }

  public setInstantWinnerData(data: GameScratchInstantWin) {
    super.setInstantWinnerData(data);

    const winnerImageId = data['winner_image_id'];
    const winnerImage = data['winner_image'];

    if (winnerImageId) {
      this.state.winnerImageId = String(winnerImageId);
    }

    if (winnerImage) {
      this.state.winnerImage = winnerImage;
    }
  }

  public hasInstantWin(): boolean {
    // we always need to call the auth api, instantWin condition uses. and returns if the player is a winner, on % chance aswell.
    return true;
  }

  public getIndicators(): GameIndicator[] {
    return [];
  }

  public isGameValid(): boolean {
    // is valid logic here
    // const data = this.getData();
    return true;
  }

  authorSignature(): string {
    return 'Jannik Fischer';
  }
}
